import {
  AppstoreOutlined,
  BankOutlined,
  CalendarOutlined,
  CarOutlined,
  ClockCircleOutlined,
  DashboardOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  FieldTimeOutlined,
  FileProtectOutlined,
  FolderOutlined,
  GlobalOutlined,
  IdcardOutlined,
  NodeIndexOutlined,
  PercentageOutlined,
  SafetyCertificateOutlined,
  SafetyOutlined,
  ScheduleOutlined,
  SecurityScanOutlined,
  SettingOutlined,
  ShopOutlined,
  StarOutlined,
  TableOutlined,
  TeamOutlined,
  ToolOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import React from 'react';
import type { ResourceProps } from '@refinedev/core';
import { ROUTES } from '@/constants';

// Refine builds the Sider from this order: a parent sits where it is declared, and its
// children follow in declaration order.
export const resources: Array<ResourceProps> = [
  {
    name: 'dashboard',
    list: '/dashboard',
    meta: {
      label: 'Dashboard',
      icon: React.createElement(DashboardOutlined),
    },
  },
  {
    name: 'calendar',
    list: '/calendar',
    meta: {
      label: 'Calendar',
      icon: React.createElement(CalendarOutlined),
    },
  },
  {
    name: 'reservations',
    list: '/reservations',
    create: '/reservations/create',
    edit: '/reservations/edit/:id',
    show: '/reservations/show/:id',
    meta: {
      label: 'Reservations',
      icon: React.createElement(ScheduleOutlined),
    },
  },
  {
    name: 'customers',
    list: '/customers',
    create: '/customers/create',
    edit: '/customers/edit/:id',
    show: '/customers/show/:id',
    meta: {
      label: 'Customers',
      icon: React.createElement(TeamOutlined),
    },
  },
  {
    name: 'setup',
    list: ROUTES.SETUP,
    meta: {
      label: 'Setup',
      icon: React.createElement(ToolOutlined),
    },
  },
  {
    name: 'fleet',
    meta: {
      label: 'Fleet',
      icon: React.createElement(CarOutlined),
    },
  },
  {
    name: 'vehicle-groups',
    list: '/vehicle-groups',
    create: '/vehicle-groups/create',
    edit: '/vehicle-groups/edit/:id',
    show: '/vehicle-groups/show/:id',
    meta: {
      label: 'Vehicle groups',
      icon: React.createElement(FolderOutlined),
      parent: 'fleet',
    },
  },
  {
    name: 'vehicle-models',
    list: '/vehicle-models',
    create: '/vehicle-models/create',
    edit: '/vehicle-models/edit/:id',
    show: '/vehicle-models/show/:id',
    meta: {
      label: 'Vehicle models',
      icon: React.createElement(AppstoreOutlined),
      parent: 'fleet',
    },
  },
  {
    name: 'vehicles',
    list: '/vehicles',
    create: '/vehicles/create',
    edit: '/vehicles/edit/:id',
    show: '/vehicles/show/:id',
    meta: {
      label: 'Vehicles',
      icon: React.createElement(CarOutlined),
      parent: 'fleet',
    },
  },
  {
    name: 'pricing',
    meta: {
      label: 'Pricing',
      icon: React.createElement(DollarOutlined),
    },
  },
  {
    name: 'stations',
    list: '/stations',
    create: '/stations/create',
    edit: '/stations/edit/:id',
    show: '/stations/show/:id',
    meta: {
      label: 'Stations',
      icon: React.createElement(EnvironmentOutlined),
      parent: 'pricing',
    },
  },
  {
    name: 'seasons',
    list: '/seasons',
    create: '/seasons/create',
    edit: '/seasons/edit/:id',
    show: '/seasons/show/:id',
    meta: {
      label: 'Seasons',
      icon: React.createElement(CalendarOutlined),
      parent: 'pricing',
    },
  },
  {
    name: 'daily-rates',
    list: ROUTES.DAILY_RATES,
    meta: {
      label: 'Rate matrix',
      icon: React.createElement(TableOutlined),
      parent: 'pricing',
    },
  },
  {
    name: 'extras',
    list: '/extras',
    create: '/extras/create',
    edit: '/extras/edit/:id',
    show: '/extras/show/:id',
    meta: {
      label: 'Extras',
      icon: React.createElement(StarOutlined),
      parent: 'pricing',
      // Tenants can only view (list/show): CRUD is platform-admin only.
    },
  },
  {
    name: 'insurances',
    list: '/insurances',
    create: '/insurances/create',
    edit: '/insurances/edit/:id',
    show: '/insurances/show/:id',
    meta: {
      label: 'Insurances',
      icon: React.createElement(SafetyOutlined),
      parent: 'pricing',
      // Tenants can only view (list/show): CRUD is platform-admin only.
    },
  },
  {
    name: 'discounts',
    list: ROUTES.DISCOUNTS,
    meta: {
      label: 'Discounts',
      icon: React.createElement(PercentageOutlined),
      parent: 'pricing',
    },
  },
  {
    name: 'other-policies',
    meta: {
      label: 'Policies',
      icon: React.createElement(FileProtectOutlined),
    },
  },
  {
    name: 'office-hours-policies',
    list: ROUTES.OFFICE_HOURS,
    meta: {
      label: 'Opening hours',
      icon: React.createElement(ClockCircleOutlined),
      parent: 'other-policies',
    },
  },
  {
    name: 'extra-driver-policies',
    list: ROUTES.EXTRA_DRIVERS,
    meta: {
      label: 'Extra drivers',
      icon: React.createElement(UserAddOutlined),
      parent: 'other-policies',
    },
  },
  {
    name: 'age-limit-policies',
    list: ROUTES.DRIVER_AGE,
    meta: {
      label: 'Driver age',
      icon: React.createElement(IdcardOutlined),
      parent: 'other-policies',
    },
  },
  {
    name: 'road-assistance-policies',
    list: ROUTES.ROAD_ASSISTANCE,
    meta: {
      label: 'Road assistance',
      icon: React.createElement(SafetyCertificateOutlined),
      parent: 'other-policies',
    },
  },
  {
    name: 'km-policies',
    list: ROUTES.KM_POLICIES,
    meta: {
      label: 'Km limits',
      icon: React.createElement(NodeIndexOutlined),
      parent: 'other-policies',
    },
  },
  {
    name: 'rental-duration-policies',
    list: ROUTES.RENTAL_DURATION,
    meta: {
      label: 'Rental duration',
      icon: React.createElement(FieldTimeOutlined),
      parent: 'other-policies',
    },
  },
  {
    name: 'rental',
    meta: {
      label: 'Rental',
      icon: React.createElement(ShopOutlined),
    },
  },
  // The users of the Rental: a Manager, or an Admin impersonating. Employees cannot
  // read the list (accessControlProvider's ADMIN_OR_MANAGER_RESOURCES).
  {
    name: 'users',
    list: ROUTES.USERS,
    show: ROUTES.USERS_SHOW,
    meta: {
      label: 'Users',
      icon: React.createElement(IdcardOutlined),
      parent: 'rental',
    },
  },
  {
    name: 'rental-profile',
    list: ROUTES.RENTAL_PROFILE,
    meta: {
      label: 'Rental profile',
      icon: React.createElement(ShopOutlined),
      parent: 'rental',
    },
  },
  // Pricing settings: a Manager, or an Admin impersonating (ADMIN_OR_MANAGER_RESOURCES).
  {
    name: 'settings',
    list: ROUTES.SETTINGS,
    meta: {
      label: 'Pricing settings',
      icon: React.createElement(SettingOutlined),
      parent: 'rental',
    },
  },
  // Station fee-templates resource kept for API operations only (no sidebar navigation)
  // Station fee templates are managed inline on the Station Fees page
  {
    name: 'fee-templates',
    meta: {
      hide: true, // Hide from sidebar - managed inline on Station Fees page
    },
  },
  {
    name: 'rentals',
    list: '/rentals',
    show: '/rentals/show/:id',
    meta: {
      label: 'Rentals',
      icon: React.createElement(BankOutlined),
      // Platform screen: only an Admin in Global view sees it (PLATFORM_RESOURCES in
      // accessControlProvider). PlatformRouteGuard refuses it during an Impersonation.
    },
  },
  {
    name: 'global-vehicle-models',
    list: ROUTES.GLOBAL_VEHICLE_MODELS,
    create: ROUTES.GLOBAL_VEHICLE_MODELS_CREATE,
    edit: ROUTES.GLOBAL_VEHICLE_MODELS_EDIT,
    show: ROUTES.GLOBAL_VEHICLE_MODELS_SHOW,
    meta: {
      label: 'Global vehicle models',
      icon: React.createElement(AppstoreOutlined),
      // Platform templates: only an Admin in Global view sees them. Rentals copy them
      // through "Start from global list" on the vehicle model create page.
    },
  },
  {
    name: 'admin-users',
    list: ROUTES.ADMIN_USERS,
    show: ROUTES.ADMIN_USERS_SHOW,
    meta: {
      label: 'All users',
      icon: React.createElement(GlobalOutlined),
      // Platform admin only: every user of every rental, and the platform admins.
    },
  },
  {
    name: 'security-events',
    list: ROUTES.SECURITY_EVENTS,
    meta: {
      label: 'Security Events',
      icon: React.createElement(SecurityScanOutlined),
      // Platform screen, read-only: the same PLATFORM_RESOURCES gate as Rentals.
    },
  },
];
